import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { InputFieldComponent, TitleEnum } from '@zayan-erp/shared';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Validators } from '@angular/forms';
import { Prospect, ProspectContact } from '../../models/prospect.models';
import { SalesService } from '../../services/sales.service';
import { GenericDropdownComponent } from '@zayan-erp/shared';
import { PhoneNumberFieldComponent } from '@zayan-erp/shared';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import {
  selectSalesOffices,
  selectSalesOfficesPublic,
  selectSalesAgents,
  selectAgencyRealtors,
  loadSalesAgents,
  loadAgencyRealtors,
  selectCountries,
  selectCitiesByCountryId
} from '@zayan-erp/stores';
import { Store } from '@ngrx/store';
import { FileDropComponent } from '@zayan-erp/shared';
import { GalleryImage } from '@zayan-erp/shared';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FileUploadService } from '@zayan-erp/shared';
import { HttpEventType } from '@angular/common/http';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { API_CONFIG } from '@zayan-erp/shared';
import { ActivatedRoute } from '@angular/router';
import { AgencyRealtorService } from '../../services/agency-realtor.service';
import * as HomeBuildingActions from '@zayan-erp/stores';
import { UserAuthenticationService } from '@zayan-erp/administration';
import { CityService } from 'libs/home-building/src/lib/services/city.service';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmHelperService } from '@zayan-erp/shared';

@Component({
  selector: 'prospect-form',
  imports: [
    InputFieldComponent,
    DatePickerModule,
    GenericDropdownComponent,
    PhoneNumberFieldComponent,
    FileDropComponent,
    ReactiveFormsModule,
    ConfirmDialogModule
  ],
  templateUrl: './prospect-form.component.html',
  styleUrl: './prospect-form.component.scss',
  providers: [],
  standalone: true
})
export class ProspectFormComponent implements AfterViewInit, OnDestroy {
  #destroyRef = inject(DestroyRef);
  #salesService = inject(SalesService);
  #store = inject(Store);
  #fileUploadService = inject(FileUploadService);
  #apiConfig = inject(API_CONFIG);
  #route = inject(ActivatedRoute);
  #agencyRealtorService = inject(AgencyRealtorService);
  #userAuthenticationService = inject(UserAuthenticationService);
  #cityService = inject(CityService);
  #confirmationService = inject(ConfirmHelperService);

  selectedProspectInput = model<string | null>();
  newProspect = model<string | null>();
  scanCards = model<boolean>(false);

  selectedSalesOfficeInput = input<string | null>();
  enableRecaptcha = input<boolean>(false);
  enableCardScan = input<boolean>(true);
  onSubmit = input.required<Observable<void>>();

  reload = output<boolean>();

  // reCAPTCHA configuration
  recaptchaWidgetId = signal<number | null>(null);
  selectedProspect = signal<string | null>(null);
  frontImage = signal<(GalleryImage & { progress: number | null; s3Key: string; documentId: string }) | null>(null);
  backImage = signal<(GalleryImage & { progress: number | null; s3Key: string; documentId: string }) | null>(null);

  salesOfficeId = toSignal(this.#salesService.saleOfficeId$, { initialValue: null });
  selectedSalesOffice = computed<string | null>(() => {
    return this.selectedSalesOfficeInput() ?? this.salesOfficeId();
  });

  officeId = toSignal(this.#route.queryParamMap.pipe(map(params => params.get('salesOfficeId'))), {
    initialValue: null
  });

  editRxResource = rxResourceFixed({
    params: () => {
      return {
        id: this.selectedProspect()
      };
    },
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#salesService.readProspectById(params.id).pipe(
          tap(prospectData => {
            this.editProspect(prospectData);
          })
        );
      }
      return of(null);
    }
  });

  private readonly recaptchaSiteKey = this.#apiConfig.recaptchaSiteKey;
  private readonly appName = this.#apiConfig.appName;
  isPublic = input<'public' | null>(null);
  maxDate = new Date();
  titlesList = computed(() =>
    Object.values(TitleEnum).map(title => ({
      id: title,
      firstLabel: title
    }))
  );

  prospectForm = new FormGroup({
    title: new FormControl<string | null>(null, []),
    salesOfficeId: new FormControl<string | null>(null, [Validators.required]),
    firstName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(50)]),
    middleName: new FormControl<string | null>(null, [Validators.maxLength(50)]),
    lastName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(50)]),
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
    ssn: new FormControl<string | null>(null, []),
    dob: new FormControl<Date | null>(null, []),
    phones: new FormControl<ProspectContact[] | null>(null, []),
    salesAgentId: new FormControl<string | null>(this.#userAuthenticationService.getUser().id ?? null, []),
    realtorId: new FormControl<string | null>(null, []),
    agencyName: new FormControl<string | null>({ value: null, disabled: true }),
    agencyAddress: new FormControl<string | null>({ value: null, disabled: true }),
    documents: new FormControl<
      { frontS3DocumentId: string | null; backS3DocumentId: string | null; documentTypeId: string }[] | null
    >(null, []),
    address: new FormGroup({
      addressLine1: new FormControl<string | null>(null, [Validators.required]),
      addressLine2: new FormControl<string | null>(null),
      suite: new FormControl<string | null>(null),
      countryId: new FormControl<string | null>(null),
      provinceId: new FormControl<string | null>(null),
      cityId: new FormControl<string | null>(null, [Validators.required]),
      postalCode: new FormControl<string | null>(null, [Validators.required])
    }),
    recaptchaToken: new FormControl<string | null>(null)
  });

  salesOffices$ = this.#store.select(this.appName === 'public' ? selectSalesOfficesPublic : selectSalesOffices);
  #salesOfficesSignal = toSignal(this.salesOffices$, { initialValue: [] });
  salesOfficesFiltered = computed(
    () =>
      this.#salesOfficesSignal().filter(
        office => office.id === (this.selectedSalesOffice() ?? this.salesOfficeId() ?? null)
      ) ?? []
  );
  salesAgents = this.#store.selectSignal(selectSalesAgents);
  agencyRealtors = this.#store.selectSignal(selectAgencyRealtors);
  countries$ = this.#store.selectSignal(selectCountries);
  cities$ = this.#store.selectSignal(selectCitiesByCountryId());
  selectedCountryId = toSignal(this.prospectForm.get('address.countryId')!.valueChanges, { initialValue: null });
  selectedProvinceId = toSignal(this.prospectForm.get('address.provinceId')!.valueChanges, { initialValue: null });
  selectedCityId = toSignal(this.prospectForm.get('address.cityId')!.valueChanges, { initialValue: null });

  provincesByCountryId = rxResourceFixed({
    params: () => ({ countryId: this.selectedCountryId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.countryId) {
        return this.#cityService.readProvinces(params.countryId);
      }
      return of([]);
    }
  });
  citiesByProvinceId = rxResourceFixed({
    params: () => ({ provinceId: this.selectedProvinceId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.provinceId) {
        return this.#cityService.readCitiesByProvinceId(params.provinceId);
      }
      return of([]);
    }
  });
  citiesByProvinceIdDisplayList = computed(() => this.citiesByProvinceId.value() ?? []);
  provincesByCountryIdDisplayList = computed(() => this.provincesByCountryId.value() ?? []);
  salesAgentsDisplayList = computed(() =>
    (this.salesAgents() || []).map(a => ({ id: a.id, firstLabel: a.firstLabel, secondLabel: a.secondLabel }))
  );
  agencyRealtorsDisplayList = computed(() =>
    (this.agencyRealtors() || []).map(r => ({ id: r.id, firstLabel: r.firstLabel, secondLabel: r.secondLabel }))
  );

  private realtorIdControl = this.prospectForm.get('realtorId') as FormControl<string | null>;
  selectedAgencyRealtorId = toSignal(this.realtorIdControl.valueChanges, {
    initialValue: this.realtorIdControl.value
  });
  realtorDetailsResource = rxResourceFixed({
    params: () => ({ id: this.selectedAgencyRealtorId() }),
    defaultValue: null,
    stream: ({ params }) => {
      return params.id ? this.#agencyRealtorService.getAgencyRealtorById(params.id) : of(null);
    }
  });
  selectedAgencyName = computed(
    () => this.realtorDetailsResource.value()?.agency?.legalName ?? this.realtorDetailsResource.value()?.agency?.name
  );

  selectedAgencyAddress = computed(() => {
    const address = this.realtorDetailsResource.value()?.agency?.address;
    return [address?.addressLine1, address?.addressLine2].filter(Boolean).join(' ');
  });

  updateAgencyFieldsEffect = effect(() => {
    const name = this.selectedAgencyName();

    const address = this.selectedAgencyAddress();
    this.prospectForm.get('agencyName')?.setValue(name || null, { emitEvent: false });
    this.prospectForm.get('agencyAddress')?.setValue(address || null, { emitEvent: false });
  });

  disableSalesOfficeControlEffect = effect(() => {
    const isPublicApp = this.isPublic();
    const preselected = this.selectedSalesOffice();
    const control = this.prospectForm.get('salesOfficeId');
    if (preselected) {
      // control?.disable({ emitEvent: false });
      // ensure value is set if not already
      if (control?.value !== preselected) {
        control?.setValue(preselected, { emitEvent: false });
      }
    } else {
      if (!isPublicApp || !this.selectedSalesOfficeInput()) {
        control?.enable({ emitEvent: false });
      }
    }
  });

  constructor() {
    this.prospectForm.get('realtorId')?.valueChanges.subscribe();
    this.prospectForm.get('address.countryId')?.valueChanges.subscribe((countryId: any) => {
      this.prospectForm.get('address.cityId')?.setValue(null);
      this.prospectForm.get('address.provinceId')?.setValue(null);
      this.#store.dispatch(HomeBuildingActions.loadCitiesByCountryId({ countryId }));
    });
    this.prospectForm.get('address.provinceId')?.valueChanges.subscribe((provinceId: any) => {
      this.prospectForm.get('address.cityId')?.setValue(null);
    });
    effect(() => {
      if (this.selectedSalesOffice()) {
        this.prospectForm.patchValue({
          salesOfficeId: this.selectedSalesOffice()
        });
      }
      if (this.selectedProspectInput()) {
        this.selectedProspect.set(this.selectedProspectInput() ?? null);
        // this.editRxResource.reload();
      }
    });

    if (this.appName === 'public') this.prospectForm.get('recaptchaToken')?.addValidators([Validators.required]);
    // Only load sales agents and agency realtors if user is authenticated
    if (this.appName !== 'public' && this.#userAuthenticationService.isLogin()) {
      this.#store.dispatch(loadSalesAgents());
    }

    const preselectedSalesOfficeId =
      this.selectedSalesOfficeInput() ?? toSignal(this.#salesService.saleOfficeId$, { initialValue: null })() ?? null;
    if (preselectedSalesOfficeId) {
      const control = this.prospectForm.get('salesOfficeId');
      control?.setValue(preselectedSalesOfficeId, { emitEvent: false });
      control?.disable({ emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    if (this.enableRecaptcha()) {
      setTimeout(() => {
        this.initializeRecaptcha();
      }, 1000);
    }
    if (!this.isPublic()) {
      this.#store.dispatch(loadAgencyRealtors());
    }

    this.onSubmit()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (this.scanCards()) {
          this.submitImages(new Event('submit'));
        } else {
          this.submitProspectsForm();
        }
      });
  }

  ngOnDestroy(): void {
    // Cleanup recaptcha widget if exists
    if (this.recaptchaWidgetId() !== null) {
      window.grecaptcha?.reset(this.recaptchaWidgetId()!);
    }
  }

  private initializeRecaptcha(): void {
    if (typeof window !== 'undefined' && window.grecaptcha) {
      window.grecaptcha.ready(() => {
        const widgetId = window.grecaptcha.render('recaptcha-container', {
          sitekey: this.recaptchaSiteKey,
          size: 'normal',
          callback: (response: string) => {
            this.prospectForm.get('recaptchaToken')?.setValue(response);
          },
          'expired-callback': () => {
            this.prospectForm.get('recaptchaToken')?.setValue(null);
          },
          'error-callback': () => {
            this.prospectForm.get('recaptchaToken')?.setValue(null);
            alert('Error in reCAPTCHA');
          }
        });
        this.recaptchaWidgetId.set(widgetId);
      });
    }
  }

  private validateRecaptcha(): boolean {
    // If reCAPTCHA is disabled, always return true
    if (!this.enableRecaptcha()) {
      return true;
    }

    const response = this.prospectForm.get('recaptchaToken')?.value;
    return typeof response === 'string' && response.length > 0;
  }

  private resetRecaptcha(): void {
    if (this.enableRecaptcha() && this.recaptchaWidgetId() !== null) {
      window.grecaptcha?.reset(this.recaptchaWidgetId()!);
      this.prospectForm.get('recaptchaToken')?.setValue(null);
    }
  }

  /** Drops null/empty/whitespace-only entries so the phone UI only shows real numbers. */
  private sanitizeProspectPhones(
    phones: { phoneNumber?: string | null; type?: string | null }[] | null | undefined
  ): ProspectContact[] {
    return (phones ?? [])
      .filter(phone => {
        const n = phone?.phoneNumber;
        return typeof n === 'string' && n.trim().length > 0;
      })
      .map(phone => ({
        phoneNumber: phone.phoneNumber!.trim(),
        type: phone.type ?? 'mobile'
      }));
  }

  editProspect(prospectData: Prospect) {
    this.prospectForm.patchValue({
      title: prospectData.title || null,
      salesOfficeId: prospectData.salesOfficeId || null,
      firstName: prospectData.firstName || null,
      middleName: prospectData.middleName || null,
      lastName: prospectData.lastName || null,
      email: prospectData.email || null,
      ssn: prospectData.ssn || null,
      salesAgentId: (prospectData as any).salesAgentId || null,
      realtorId: (prospectData as any).realtorId || null,
      address: {
        addressLine1: prospectData.address?.addressLine1 || null,
        addressLine2: prospectData.address?.addressLine2 || null,
        postalCode: prospectData.address?.postalCode || null,
        countryId: prospectData.countryId || null,
        provinceId: prospectData.address?.city?.provinceId || null,
        cityId: prospectData.address?.cityId || null,
        suite: prospectData.address?.suite || null
      },
      dob: prospectData.dateOfBirth
        ? (() => {
            const d = new Date(prospectData.dateOfBirth);
            return Number.isNaN(d.getTime()) ? null : d;
          })()
        : null,
      phones: this.sanitizeProspectPhones(prospectData.phones),
      documents: prospectData.documents.map(document => ({
        frontS3DocumentId: document.frontS3DocumentId,
        backS3DocumentId: document.backS3DocumentId,
        documentTypeId: document.documentTypeId
      }))
    });
    // Ensure the form is pristine/untouched after initializing with loaded data
    this.prospectForm.markAsPristine();
    this.prospectForm.markAsUntouched();
  }

  submitProspectsForm() {
    if (this.enableRecaptcha() && !this.validateRecaptcha()) {
      console.error('Please complete the reCAPTCHA verification');
      this.prospectForm.markAllAsTouched();
      alert('Please complete the reCAPTCHA verification');
      return;
    }

    if (this.prospectForm.valid) {
      const formData = this.prospectForm.getRawValue();

      const prospectData = {
        title: formData.title || null,
        salesOfficeId: formData.salesOfficeId!,
        firstName: formData.firstName!,
        middleName: formData.middleName || null,
        lastName: formData.lastName!,
        email: formData.email!,
        ssn: formData.ssn || null,
        ...(!this.isPublic()
          ? {
              salesAgentId: formData.salesAgentId ?? null,
              realtorId: formData.realtorId ?? null
            }
          : {}),
        address: {
          addressLine1: formData.address.addressLine1!,
          addressLine2: formData.address.addressLine2!,
          postalCode: formData.address.postalCode!,
          countryId: formData.address.countryId!,
          cityId: formData.address.cityId!,
          suite: formData.address.suite!
        },
        dob: formData.dob instanceof Date && !Number.isNaN(formData.dob.getTime()) ? formData.dob.toISOString() : null,
        phones: this.sanitizeProspectPhones(formData.phones),
        documents: formData.documents?.map(document => ({
          frontS3DocumentId: document.frontS3DocumentId!,
          backS3DocumentId: document.backS3DocumentId!,
          documentTypeId: document.documentTypeId!
        }))
      };

      if (this.selectedProspect()) {
        const updateData: any = {
          id: this.selectedProspect()!,
          ...prospectData
        };

        this.#salesService.updateProspect(this.selectedProspect()!, updateData).subscribe({
          next: () => {
            this.resetRecaptcha();
            this.newProspect.set(null);
            this.editRxResource.reload();
            this.reload.emit(true);
          }
        });
      } else {
        if (this.isPublic()) {
          this.#salesService
            .createProspectPublic(prospectData, this.prospectForm.get('recaptchaToken')?.value!)
            .subscribe({
              next: () => {
                this.onProspectFormSave();
              }
            });
        } else {
          this.#salesService.createProspect(prospectData).subscribe({
            next: () => {
              this.onProspectFormSave();
            }
          });
        }
      }
    } else {
      this.prospectForm.markAllAsTouched();
    }
  }

  onProspectFormSave(): void {
    this.prospectForm.reset();
    this.resetRecaptcha();
    this.selectedProspect.set(null);
    this.newProspect.set(null);
    this.reload.emit(true);
  }

  submitImages(event: Event) {
    event.preventDefault();
    if (!this.frontImage()) {
      // this.#toaster.warning('Please select at least the front image to scan');
      return;
    }

    const imagesToUpload = [
      { type: 'front' as const, image: this.frontImage() },
      { type: 'back' as const, image: this.backImage() }
    ];
    const uploadObservables: Observable<any>[] = imagesToUpload
      .filter(item => !!item.image)
      .map(item =>
        this.#fileUploadService.getPrivateAWSSignedUrl(item.image!.file!).pipe(
          switchMap(awsData =>
            this.#fileUploadService.uploadFileToS3(awsData).pipe(
              tap((event: any) => {
                if (event?.type === HttpEventType.UploadProgress && event.total) {
                  const progressValue = Math.round((100 * event.loaded) / event.total);
                  if (item.type === 'front') {
                    this.frontImage.set({
                      ...this.frontImage()!,
                      progress: progressValue,
                      s3Key: awsData.s3Key,
                      documentId: awsData.documentId
                    });
                  } else {
                    this.backImage.set({
                      ...this.backImage()!,
                      progress: progressValue,
                      s3Key: awsData.s3Key,
                      documentId: awsData.documentId
                    });
                  }
                }
              }),
              map(() => awsData)
            )
          )
        )
      );

    if (uploadObservables.length === 0) {
      return;
    }

    forkJoin(uploadObservables).subscribe({
      next: (uploadResults: any[]) => {
        const urlObservables = uploadResults.map(r =>
          this.#fileUploadService.getPresignedUrlFromS3Key(r.s3Key, r.fileName)
        );

        forkJoin(urlObservables).subscribe({
          next: (imageUrls: string[]) => {
            const frontImageUrl = imageUrls[0];
            const backImageUrl = imageUrls.length > 1 ? imageUrls[1] : undefined;
            this.#salesService.scanIdentityCard(frontImageUrl, backImageUrl).subscribe({
              next: scanResult => {
                this.scanCards.set(false);
                this.insertScannedDetails(scanResult, this.frontImage()?.documentId, this.backImage()?.documentId);
                this.frontImage.set(null);
                this.backImage.set(null);
                this.#confirmationService.showModal({
                  icon: 'confirm-icon',
                  header: 'Verify Details',
                  message: 'Please verify the scanned details before saving the prospect.',
                  acceptLabel: 'OK',
                  acceptSeverity: 'primary',
                  rejectLabel: null
                });
              }
            });
          }
        });
      }
    });
  }

  insertScannedDetails(scanResult: any, frontDocumentId?: string, backDocumentId?: string) {
    // Find country by matching countryCode
    let countryId: string | null = null;
    if (scanResult.data?.countryCode) {
      const countryCode = scanResult.data.countryCode.trim().toUpperCase();
      const countries = this.countries$();

      // Try exact match first (case-insensitive)
      const exactMatch = countries.find(country => country.secondLabel?.trim().toUpperCase() === countryCode);

      if (exactMatch) {
        countryId = exactMatch.id;
      } else {
        // Fall back to partial match (case-insensitive)
        const partialMatch = countries.find(
          country =>
            country.secondLabel?.trim().toUpperCase().includes(countryCode) ||
            countryCode.includes(country.secondLabel?.trim().toUpperCase() ?? '')
        );
        countryId = partialMatch?.id ?? null;
      }
    }

    this.prospectForm.patchValue({
      title: scanResult.data.title ?? null,
      firstName: scanResult.data.firstName,
      middleName: scanResult.data.middleName ?? null,
      lastName: scanResult.data.lastName,
      ssn: scanResult.data.ssn ?? null,
      address: {
        addressLine1: scanResult.data?.addressLine1,
        addressLine2: scanResult.data?.addressLine2,
        postalCode: scanResult.data?.zipCode,
        countryId: scanResult.data.countryId,
        provinceId: scanResult.data.provinceId,
        cityId: scanResult.data?.cityId,
        suite: scanResult.data?.suite
      },
      email: scanResult.data?.email ?? null,
      ...(scanResult.data?.phoneNumber
        ? {
            phones: [
              {
                phoneNumber: scanResult.data?.phoneNumber,
                type: 'Mobile'
              }
            ]
          }
        : {}),
      dob: scanResult.data.dateOfBirth ? new Date(scanResult.data.dateOfBirth) : null,
      documents: [
        {
          frontS3DocumentId: frontDocumentId ?? null,
          backS3DocumentId: backDocumentId ?? null,
          documentTypeId: scanResult?.documentTypeId
        }
      ]
    });
  }

  onFileChange(event: Event, type: 'front' | 'back') {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (type === 'front') {
        this.frontImage.set({
          file,
          fileSrcUrl: URL.createObjectURL(file),
          selected: false,
          progress: null,
          s3Key: '',
          documentId: ''
        });
      } else {
        this.backImage.set({
          file,
          fileSrcUrl: URL.createObjectURL(file),
          selected: false,
          progress: null,
          s3Key: '',
          documentId: ''
        });
      }
      if (this.frontImage()) {
        this.submitImages(new Event('submit'));
      }
    }
  }

  onFileDrop(event: NgxFileDropEntry[], type: 'front' | 'back') {
    for (const file of event) {
      if (file.fileEntry.isFile) {
        const fileEntry = file.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (type === 'front') {
            this.frontImage.set({
              file,
              fileSrcUrl: URL.createObjectURL(file),
              selected: false,
              progress: null,
              s3Key: '',
              documentId: ''
            });
          } else {
            this.backImage.set({
              file,
              fileSrcUrl: URL.createObjectURL(file),
              selected: false,
              progress: null,
              s3Key: '',
              documentId: ''
            });
          }
          if (this.frontImage()) {
            this.submitImages(new Event('submit'));
          }
        });
      }
    }
  }
}
